import type { Localized, MediaRef } from "../types";

/**
 * Cookie consent data model (docs/cookie-consent-plan.md §3). Browser-safe:
 * imported by the consent runtime, the admin and the server register.
 */

export const CONSENT_CATEGORIES = ["necessary", "functional", "analytics", "marketing"] as const;
export type ConsentCategory = (typeof CONSENT_CATEGORIES)[number];
/** Optional categories a visitor can switch (necessary is always on). */
export const OPTIONAL_CATEGORIES = ["functional", "analytics", "marketing"] as const;
export type OptionalCategory = (typeof OPTIONAL_CATEGORIES)[number];

/**
 * exempt: Telecommunicatiewet 11.7a lid 3 (no consent needed; e.g. cookieless first-party statistics).
 * legitimate_interest: allowed by default, visitor can object.
 * consent: off until the visitor allows it.
 */
export type ConsentBasis = "exempt" | "legitimate_interest" | "consent";

export type GoogleConsentType = "ad_storage" | "analytics_storage" | "ad_user_data" | "ad_personalization" | "functionality_storage" | "personalization_storage" | "security_storage";

export interface ConsentCookie {
  name: string;
  /** Prefix match (`_ga_` matches `_ga_ABC123`). */
  pattern?: boolean;
  storage?: "cookie" | "localStorage" | "sessionStorage" | "indexedDB";
  /** Set when the cookie lives on a third-party domain (can't be deleted by the site). */
  domain?: string;
  retention: Localized | string;
  httpOnly?: boolean;
  purpose?: Localized | string;
}

export interface ConsentProvider {
  name: string;
  country?: string;
  privacyUrl?: string;
}

export interface ConsentService {
  id: string;
  source: "core" | "preset" | "module" | "theme" | "manual" | "scan";
  name: string;
  provider: ConsentProvider;
  category: ConsentCategory;
  basis: ConsentBasis;
  purpose: Localized | string;
  /** Personal data shared with the provider, in plain words. */
  dataShared?: Localized | string;
  /** Data leaves the EU/EEA. */
  thirdCountry?: boolean;
  cookies: ConsentCookie[];
  /** Network hosts this service loads from (blocking guard + scanner matching). */
  hosts?: string[];
  googleConsent?: GoogleConsentType[];
  /** Module/theme id for module/theme services. */
  owner?: string;
}

/** Someone who processes personal data for the site owner (hosting, mail…), listed in the privacy statement. */
export interface ConsentProcessor {
  id: string;
  name: string;
  role: Localized | string;
  country?: string;
  dataCategories?: Localized | string;
}

/** A configured integration preset (GA4, Meta Pixel…). The id format is validated per preset. */
export interface IntegrationSetting {
  preset: string;
  value: string;
  enabled?: boolean;
}

/** A script pasted by the owner, always tied to a service (and so a category). */
export interface CustomScript {
  id: string;
  label: string;
  serviceId: string;
  placement: "head" | "body";
  /** Raw HTML: only <script>/<noscript> tags survive; non-necessary services are rendered inert. */
  code: string;
  enabled?: boolean;
}

export interface BannerTexts {
  title?: string;
  body?: string;
  acceptAll?: string;
  rejectAll?: string;
  preferences?: string;
  save?: string;
  floatingLabel?: string;
}

export interface ConsentSettings {
  /** Banner copy per locale; empty strings fall back to the built-in AP-aligned defaults. */
  texts?: Record<string, BannerTexts>;
  layout?: "bar" | "box" | "center";
  floatingButton?: boolean;
  floatingPosition?: "left" | "right";
  expiryDays?: number;
  honorGpc?: boolean;
  /** Bumped by "ask everyone again". */
  revisionCounter?: number;
  integrations?: IntegrationSetting[];
  googleConsentAdvanced?: boolean;
  customScripts?: CustomScript[];
  manualServices?: ConsentService[];
  /** Owner classification of scan findings: cookie name → service id (or "ignore"). */
  classifications?: Record<string, string>;
  /** Hosts allowed in embeds without a facade (owner decision). */
  embedAllowHosts?: string[];
  /** Log retention in months (default 24). */
  logRetentionMonths?: number;
  colors?: { background?: string; text?: string; button?: string; buttonText?: string };
  /** When the owner last confirmed the generated privacy statement. */
  policyReviewedAt?: string;
}

export interface ControllerDetails {
  organisation?: string;
  kvk?: string;
  address?: string;
  email?: string;
  phone?: string;
  dpo?: string;
  /** Free text sections around the generated policy. */
  introText?: Localized;
  extraText?: Localized;
  logo?: MediaRef | null;
}

/** What a visitor's browser stores in the cms_consent cookie. */
export interface StoredConsent {
  v: 1;
  id: string;
  rev: string;
  ts: number;
  c: Partial<Record<OptionalCategory, boolean>>;
  /** Per-service overrides (true/false), e.g. "always allow YouTube". */
  s?: Record<string, boolean>;
}

/** Client-side config embedded in every public page (identical for all visitors). */
export interface ConsentClientConfig {
  revision: string;
  locale: string;
  expiryDays: number;
  /** At least one service needs consent → show the banner to visitors without a valid choice. */
  bannerNeeded: boolean;
  honorGpc: boolean;
  layout: "bar" | "box" | "center";
  floatingButton: boolean;
  floatingPosition: "left" | "right";
  policyUrl: string | null;
  controllerName: string;
  thirdPartyCount: number;
  texts: Required<BannerTexts> & Record<string, string>;
  categories: {
    id: ConsentCategory;
    services: { id: string; name: string; provider: string; basis: ConsentBasis; purpose: string; cookies: { name: string; retention: string; domain?: string }[] }[];
  }[];
  /** Integrations the runtime loads after consent. */
  integrations: { preset: string; serviceId: string; value: string }[];
  googleConsentAdvanced: boolean;
  /** Cookie/storage names to delete per service when consent is withdrawn. */
  clear: Record<string, { cookies: string[]; prefixes: string[]; storage: string[] }>;
  /** host → service id, for the blocking guard and embeds. */
  hosts: Record<string, string>;
  colors?: ConsentSettings["colors"];
}
